import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { FailureModeTablePDF } from './FailureModeTablePDF';
import { StandardTitle } from './StandardTitle';

const styles = StyleSheet.create({
    container: {
        padding: 10,
        marginBottom: 10
    },
    heading: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    section: {
        marginBottom: 16,
    },
    row: {
        flexDirection: 'row',
        marginVertical: 4,
        borderBottom: '1pt solid #ECECEC',
    },
    labelColumn: {
        width: '40%',
        backgroundColor: '#F0F4F8',
        padding: 6,
        borderRight: '1pt solid #ECECEC',
    },
    valueColumn: {
        width: '60%',
        padding: 6,
    },
    label: {
        fontSize: 10,
        fontWeight: 'bold',
    },
    value: {
        fontSize: 10,
    },
    statusPass: {
        backgroundColor: '#4CAF50',
        color: 'white',
        padding: 4,
        borderRadius: 3,
        fontSize: 10,
        textAlign: 'center',
    },
    statusFail: {
        backgroundColor: '#F44336',
        color: 'white',
        padding: 4,
        borderRadius: 3,
        fontSize: 10,
        textAlign: 'center',
    },
    statusPending: {
        backgroundColor: '#9E9E9E',
        color: 'white',
        padding: 4,
        borderRadius: 3,
        fontSize: 10,
        textAlign: 'center',
    },
    statusConditional: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: 4,
        borderRadius: 3,
        fontSize: 10,
        textAlign: 'center',
    },
    stackTestTable: {
        marginTop: 8,
        marginBottom: 8,
    },
    newFailureModesTable: {
        marginTop: 16,
    }
});

interface SynopsisPDFProps {
    synopsisData: {
        projectName: string;
        projectDescription: string;
        overallDecision: string;
        overallDecisionFailureNote: string;
        validStackingTest: string;
        reasonForInvalidStackingTest: string;
        failureModes: Record<string, Record<string, string>>;
        newFailureModes: Array<{ failureModeName: string; status: string }>;
    };
    palletsForTestStack?: any[];
}

export const SynopsisPDF: React.FC<SynopsisPDFProps> = ({ synopsisData, palletsForTestStack = [], fullData }) => {

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'pass':
                return styles.statusPass;
            case 'fail':
                return styles.statusFail;
            case 'conditionalPass':
                return styles.statusConditional;
            default:
                return styles.statusPending;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pass':
                return 'Pass';
            case 'fail':
                return 'Fail';
            case 'conditionalPass':
                return 'Conditional Pass';
            case 'dataMissing':
                return 'Incomplete';
            default:
                return 'Pending';
        }
    };

    return (
        <View style={styles.container}>
            <StandardTitle title="Synopsis" />

            <View style={styles.section}>
                <View style={styles.row}>
                    <View style={styles.labelColumn}>
                        <Text style={styles.label}>Project Name</Text>
                    </View>
                    <View style={styles.valueColumn}>
                        <Text style={styles.value}>{synopsisData.projectName || 'N/A'}</Text>
                    </View>
                </View>

                <View style={styles.row}>
                    <View style={styles.labelColumn}>
                        <Text style={styles.label}>Project Description</Text>
                    </View>
                    <View style={styles.valueColumn}>
                        <Text style={styles.value}>{synopsisData.projectDescription || 'N/A'}</Text>
                    </View>
                </View>

                <View style={styles.row}>
                    <View style={styles.labelColumn}>
                        <Text style={styles.label}>Overall Decision</Text>
                    </View>
                    <View style={styles.valueColumn}>
                        <View style={getStatusStyle(synopsisData.overallDecision)}>
                            <Text>{getStatusText(synopsisData.overallDecision)}</Text>
                        </View>
                    </View>
                </View>

                {synopsisData.overallDecisionFailureNote && (
                    <View style={styles.row}>
                        <View style={styles.labelColumn}>
                            <Text style={styles.label}>Decision Notes</Text>
                        </View>
                        <View style={styles.valueColumn}>
                            <Text style={styles.value}>{synopsisData.overallDecisionFailureNote}</Text>
                        </View>
                    </View>
                )}
            </View>

            {/* Failure Modes Table */}
            <FailureModeTablePDF synopsisData={synopsisData} fullData={fullData} failureModes={synopsisData.failureModes} />

            {/* New Failure Modes */}
            {synopsisData.newFailureModes?.length > 0 && synopsisData.newFailureModes[0].failureModeName && (
                <View style={styles.newFailureModesTable}>
                    <Text style={styles.heading}>Additional Failure Modes</Text>
                    <View style={styles.row}>
                        <View style={{ ...styles.labelColumn, width: '60%' }}>
                            <Text style={styles.label}>Failure Mode</Text>
                        </View>
                        <View style={{ ...styles.labelColumn, width: '40%' }}>
                            <Text style={styles.label}>Status</Text>
                        </View>
                    </View>

                    {synopsisData.newFailureModes.map((failureMode, index) => (
                        failureMode.failureModeName && (
                            <View key={index} style={styles.row}>
                                <View style={{ ...styles.valueColumn, width: '60%' }}>
                                    <Text style={styles.value}>{failureMode.failureModeName}</Text>
                                </View>
                                <View style={{ ...styles.valueColumn, width: '40%' }}>
                                    <View style={getStatusStyle(failureMode.status)}>
                                        <Text>{getStatusText(failureMode.status)}</Text>
                                    </View>
                                </View>
                            </View>
                        )
                    ))}
                </View>
            )}

            {/* Stacking Test Section */}
            <View style={styles.section}>
                <Text style={styles.heading}>Stacking Test Validation</Text>

                <View style={styles.row}>
                    <View style={styles.labelColumn}>
                        <Text style={styles.label}>Is Stacking Test Valid?</Text>
                    </View>
                    <View style={styles.valueColumn}>
                        <Text style={styles.value}>{synopsisData.validStackingTest === 'yes' ? 'Yes' : 'No'}</Text>
                    </View>
                </View>

                {synopsisData.validStackingTest === 'no' && (
                    <View style={styles.row}>
                        <View style={styles.labelColumn}>
                            <Text style={styles.label}>Reason for Invalid Test</Text>
                        </View>
                        <View style={styles.valueColumn}>
                            <Text style={styles.value}>{synopsisData.reasonForInvalidStackingTest}</Text>
                        </View>
                    </View>
                )}
            </View>

            {/* Stack Test Validation Details Table */}
            {palletsForTestStack && palletsForTestStack.length > 0 && (
                <View style={styles.stackTestTable}>
                    <Text style={styles.heading}>Stack Test Details</Text>
                    <View style={styles.row}>
                        <View style={{ ...styles.labelColumn, width: '20%' }}>
                            <Text style={styles.label}>Pallet Name</Text>
                        </View>
                        <View style={{ ...styles.labelColumn, width: '20%' }}>
                            <Text style={styles.label}>Stack Pattern</Text>
                        </View>
                        <View style={{ ...styles.labelColumn, width: '20%' }}>
                            <Text style={styles.label}>Hold Duration</Text>
                        </View>
                        <View style={{ ...styles.labelColumn, width: '20%' }}>
                            <Text style={styles.label}>Min Pallets</Text>
                        </View>
                        <View style={{ ...styles.labelColumn, width: '20%' }}>
                            <Text style={styles.label}>Status</Text>
                        </View>
                    </View>

                    {palletsForTestStack.map((pallet, index) => (
                        <View key={index} style={styles.row}>
                            <View style={{ ...styles.valueColumn, width: '20%' }}>
                                <Text style={styles.value}>{pallet.name}</Text>
                            </View>
                            <View style={{ ...styles.valueColumn, width: '20%' }}>
                                <Text style={styles.value}>{pallet.stackPattern || 'N/A'}</Text>
                            </View>
                            <View style={{ ...styles.valueColumn, width: '20%' }}>
                                <Text style={styles.value}>{pallet.stackTestLayoutWeek || 'N/A'}</Text>
                            </View>
                            <View style={{ ...styles.valueColumn, width: '20%' }}>
                                <Text style={styles.value}>{pallet.recommendedMinimumPallets || 'N/A'}</Text>
                            </View>
                            <View style={{ ...styles.valueColumn, width: '20%' }}>
                                <Text style={styles.value}>
                                    {pallet.stackPattern && pallet.lpnCount < pallet.recommendedMinimumPallets
                                        ? 'Invalid'
                                        : 'Valid'}
                                </Text>
                            </View>
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
};